import {
  Alert,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { api } from '../api/client';
import type { ActivitySummaryDto } from '../api/generated/client';
import { Box, Stack, Typography } from '../components/mui-system';

const dateTime = (value: string) => new Date(value).toLocaleString('ru-RU');
export function ActivityPanel() {
  const [days, setDays] = useState(7);
  const [workspaceId, setWorkspaceId] = useState('');
  const [excludeSelf, setExcludeSelf] = useState(false);
  const [filters, setFilters] = useState({ days, workspaceId, excludeSelf });
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const [revision, setRevision] = useState(0);
  const [data, setData] = useState<ActivitySummaryDto | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const afterUserId = cursors.at(-1);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    setData(null);
    void api
      .activitySummary({ ...filters, afterUserId })
      .then(
        (result) => {
          if (active) setData(result);
        },
        (caught) => {
          if (active)
            setError(caught instanceof Error ? caught.message : 'Не удалось загрузить активность');
        },
      )
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [filters, afterUserId, revision]);
  function apply(event: FormEvent) {
    event.preventDefault();
    setCursors([undefined]);
    setFilters({ days, workspaceId: workspaceId.trim(), excludeSelf });
  }
  return (
    <Card>
      <CardContent>
        <Typography variant="h3" mb={1}>
          Активность
        </Typography>
        <Typography color="text.secondary" mb={2}>
          Посещения кабинета и разделов. Дни считаются по UTC; последнее посещение — с точностью до
          5 минут. <Link to="/docs/activity">Как считается активность</Link>
        </Typography>
        <Stack
          component="form"
          onSubmit={apply}
          direction={{ xs: 'column', sm: 'row' }}
          gap={2}
          mb={2}
          alignItems={{ sm: 'center' }}
          sx={{ flexWrap: 'wrap' }}
        >
          <TextField
            select
            size="small"
            label="Период"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
            sx={{ minWidth: 160 }}
          >
            {[1, 7, 30, 90].map((value) => (
              <MenuItem key={value} value={value}>
                {value === 1 ? 'Сегодня' : `${value} дней`}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="ID workspace"
            value={workspaceId}
            onChange={(event) => setWorkspaceId(event.target.value)}
            helperText="Пусто — все, включая общие разделы"
            sx={{ minWidth: 220 }}
          />
          <FormControlLabel
            control={
              <Checkbox checked={excludeSelf} onChange={(_, value) => setExcludeSelf(value)} />
            }
            label="Исключить мои посещения"
            sx={{ whiteSpace: 'nowrap', mr: 0 }}
          />
          <Button type="submit" disabled={loading}>
            Применить
          </Button>
          <Button
            disabled={loading}
            onClick={() => {
              setCursors([undefined]);
              setRevision((value) => value + 1);
            }}
          >
            Обновить
          </Button>
        </Stack>
        {loading && <CircularProgress size={24} aria-label="Загрузка активности" />}
        {error && <Alert severity="error">{error}</Alert>}
        {data && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} gap={4} mb={3}>
              <Box>
                <Typography variant="h2">{data.users}</Typography>
                <Typography>Уникальных пользователей</Typography>
              </Box>
              <Box>
                <Typography variant="h2">
                  {data.sections.filter((section) => section.users > 0).length}
                </Typography>
                <Typography>Посещённых разделов</Typography>
              </Box>
              <Box>
                <Typography>
                  {data.fromDay} — {data.toDay} UTC
                </Typography>
                <Typography color="text.secondary">Хранение — {data.retentionDays} дней</Typography>
              </Box>
            </Stack>
            {data.collection.failedWrites > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                После запуска сервера {dateTime(data.collection.since)} не удалось сохранить
                посещения: {data.collection.failedWrites}. Последняя ошибка:{' '}
                {data.collection.lastErrorAt ? dateTime(data.collection.lastErrorAt) : 'неизвестно'}
                . Сводка может быть неполной.
              </Alert>
            )}
            {data.users === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                За выбранный период посещений не зафиксировано. История собирается с момента
                включения учёта.
              </Alert>
            )}
            <Typography variant="h3" mb={1}>
              Люди
            </Typography>
            <Typography color="text.secondary" mb={1}>
              Показаны пользователи с зафиксированными посещениями за период.
            </Typography>
            <TableContainer>
              <Table size="small" aria-label="Активность пользователей">
                <TableHead>
                  <TableRow>
                    <TableCell>Пользователь</TableCell>
                    <TableCell>Последнее посещение</TableCell>
                    <TableCell>Дней посещений</TableCell>
                    <TableCell>Разделы</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.people.map((person) => (
                    <TableRow key={person.userId}>
                      <TableCell>
                        {person.displayName}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {person.userId}
                        </Typography>
                      </TableCell>
                      <TableCell>{dateTime(person.lastSeenAt)}</TableCell>
                      <TableCell>{person.activeDays}</TableCell>
                      <TableCell>
                        {person.sectionIds
                          .map(
                            (id) =>
                              data.sections.find((section) => section.sectionId === id)?.label ??
                              id,
                          )
                          .join(', ')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction="row" gap={2} alignItems="center" my={2}>
              <Button
                disabled={cursors.length === 1 || loading}
                onClick={() => setCursors((values) => values.slice(0, -1))}
              >
                Назад
              </Button>
              <Typography>Страница {cursors.length}</Typography>
              <Button
                disabled={!data.nextUserId || loading}
                onClick={() => setCursors((values) => [...values, data.nextUserId!])}
              >
                Далее
              </Button>
            </Stack>
            <Typography variant="h3" mb={1}>
              Разделы
            </Typography>
            <TableContainer>
              <Table size="small" aria-label="Активность разделов">
                <TableHead>
                  <TableRow>
                    <TableCell>Раздел</TableCell>
                    <TableCell>Уникальных пользователей</TableCell>
                    <TableCell>Дни посещений (UTC)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.sections.map((section) => (
                    <TableRow key={section.sectionId}>
                      <TableCell>{section.label}</TableCell>
                      <TableCell>{section.users}</TableCell>
                      <TableCell>
                        {section.days.length ? (
                          <details>
                            <summary>{section.days.length} дн.</summary>
                            {section.days.join(', ')}
                          </details>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {data.days.length > 0 && (
              <Box component="details" mt={2}>
                <summary>Пользователи по дням (UTC)</summary>
                <Table size="small" aria-label="Активность по дням">
                  <TableHead>
                    <TableRow>
                      <TableCell>Дата</TableCell>
                      <TableCell>Уникальных пользователей</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.days.map((day) => (
                      <TableRow key={day.day}>
                        <TableCell>{day.day}</TableCell>
                        <TableCell>{day.users}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
